import { Link } from "react-router-dom";
import { Facebook, Instagram, Phone, Mail, ShieldCheck, Leaf, Hammer } from "lucide-react";

// TikTok icon (not in lucide-react)
const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const SOCIAL_LINKS = {
  facebook: "https://www.facebook.com/profile.php?id=61579538215508",
  instagram: "https://www.instagram.com/repticraftstore",
  tiktok: "https://www.tiktok.com/@repticraftstore",
};

const Footer = () => {
  return (
    <footer className="bg-[#0A0A0A] border-t border-white/10">
      {/* Trust strip */}
      <div className="border-b border-white/10 bg-[#121212]/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center gap-3" data-testid="footer-warranty-badge">
            <ShieldCheck className="w-5 h-5 text-[#E07A5F] flex-shrink-0" />
            <div>
              <p className="text-[#F4F1DE] font-semibold text-sm">Lifetime Carcass Warranty</p>
              <p className="text-[#F4F1DE]/50 text-xs">Built to outlive your reptile, not break alongside it.</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-testid="footer-materials-badge">
            <Leaf className="w-5 h-5 text-green-500 flex-shrink-0" />
            <div>
              <p className="text-[#F4F1DE] font-semibold text-sm">FSC-Certified Marine Plywood</p>
              <p className="text-[#F4F1DE]/50 text-xs">Sustainable timber, sourced from UK merchants.</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-testid="footer-handcraft-badge">
            <Hammer className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div>
              <p className="text-[#F4F1DE] font-semibold text-sm">Handcrafted in the UK</p>
              <p className="text-[#F4F1DE]/50 text-xs">Made-to-order in our British workshop.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center space-x-3 mb-4">
              <img 
                src="/logo.png" 
                alt="ReptiCraft Logo" 
                className="h-14 w-auto"
              />
            </Link>
            <p className="text-[#F4F1DE]/50 text-sm mb-4">
              Bespoke reptile enclosures crafted with care and precision.
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#E07A5F] flex-shrink-0" />
                <a
                  href="tel:+447426848476"
                  className="text-[#F4F1DE]/70 hover:text-[#E07A5F] transition-colors"
                  data-testid="footer-phone-link"
                >
                  07426 848476
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#E07A5F] flex-shrink-0" />
                <a
                  href="mailto:repticraftstore@gmail.com"
                  className="text-[#F4F1DE]/70 hover:text-[#E07A5F] transition-colors break-all"
                  data-testid="footer-email-link"
                >
                  repticraftstore@gmail.com
                </a>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-[#F4F1DE] font-semibold mb-4">Products</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/configurator" className="text-[#F4F1DE]/50 hover:text-[#E07A5F] text-sm transition-colors">
                  Wood Enclosures
                </Link>
              </li>
              <li>
                <Link to="/engineered" className="text-amber-400 hover:text-amber-300 text-sm transition-colors">
                  Engineered (Budget)
                </Link>
              </li>
              <li>
                <Link to="/pvc" className="text-red-400 hover:text-red-300 text-sm transition-colors">
                  PVC Enclosures <span className="text-[10px] uppercase text-red-500">(out of stock)</span>
                </Link>
              </li>
              <li>
                <Link to="/stacking" className="text-[#F4F1DE]/50 hover:text-[#E07A5F] text-sm transition-colors">
                  Stacking Units
                </Link>
              </li>
              <li>
                <Link to="/clearance" className="text-green-400 hover:text-green-300 text-sm transition-colors">
                  ♻️ Clearance (Eco)
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-[#F4F1DE] font-semibold mb-4">Company</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-[#F4F1DE]/50 hover:text-[#E07A5F] text-sm transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-[#F4F1DE]/50 hover:text-[#E07A5F] text-sm transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/track" className="text-[#F4F1DE]/50 hover:text-[#E07A5F] text-sm transition-colors">
                  Track Order
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-[#F4F1DE] font-semibold mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy" className="text-[#F4F1DE]/50 hover:text-[#E07A5F] text-sm transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-[#F4F1DE]/50 hover:text-[#E07A5F] text-sm transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/unsubscribe" className="text-[#F4F1DE]/50 hover:text-[#E07A5F] text-sm transition-colors">
                  Unsubscribe
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 mt-8 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-[#F4F1DE]/40 text-sm">
            © {new Date().getFullYear()} ReptiCraft LTD. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a
              href={SOCIAL_LINKS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#F4F1DE]/50 hover:text-[#1877F2] transition-colors"
              aria-label="Facebook"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <a
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#F4F1DE]/50 hover:text-[#E4405F] transition-colors"
              aria-label="Instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href={SOCIAL_LINKS.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#F4F1DE]/50 hover:text-[#00f2ea] transition-colors"
              aria-label="TikTok"
            >
              <TikTokIcon />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
